import React from 'react';
import { Link as ScrollLink } from "react-scroll";
import Alert from './Alert';
import { ToSnakeCase } from './CaseManager';
import { connect } from 'react-redux';

interface Props {
    sectID?: any;
    available?: boolean;
    active?: boolean;
    itemStyle?: string;
    navLink: string;
    navItem?: string;
    icon_1?: string;
    icon_2?: string;
    style?: string;
    hidden?: boolean;
    variant?: 'bar' | 'row';
    floating?: boolean;
    floatingStyle?: string;
    toNewPage?: boolean;
    sectionInView?: string;
    onClick: ((e?: any) => void);
    action: ((e?: any) => void) | null;
    setModalState?: (e?: any) => void | null;
}

const NavItem: React.FC<Props> = (props) => {
    const isResume = props.navItem === 'My Resume';
    const isRow = props.variant === 'row';

    const handleClick = (e?: React.MouseEvent) => {
        if (!props.available) {
            e?.preventDefault();
            Alert('info', "Sorry, this feature is not available yet.");
            return;
        }

        if (props.action) {
            e?.preventDefault();
            props.action();
        }

        props.setModalState && props.setModalState(false);
    };

    const label = (
        <span className="pointer-events-none whitespace-nowrap !font-normal">
            {props.navItem}
        </span>
    );

    // react-scroll's Link renders a bare <a {...props}> and never sets href
    // itself, so without this the row is not a link and not keyboard focusable.
    const href = `#${props.sectID}`;

    // Vertical list used inside the mobile navigation panel. Deliberately
    // ignores `style`, which carries desktop bar padding that would inset the row.
    if (isRow) {
        return (
            <ScrollLink
                to={props.sectID}
                href={href}
                spy={true}
                smooth={true}
                offset={0}
                duration={1500}
                onClick={handleClick}
                className={`w-full ${props.hidden ? 'hidden' : ''} ${props.active ? 'text-Primary' : ''} cursor-pointer`}
            >
                <div
                    data-name={ToSnakeCase(props.navItem)}
                    aria-current={props.active ? 'true' : undefined}
                    className={`group relative w-full flex items-center gap-3 min-h-[48px] px-4 rounded-lg overflow-hidden transition ease-in-out duration-250 ${
                        isResume
                            ? `border ${props.active ? 'border-Primary bg-Primary text-white' : 'border-Primary text-Primary hover:bg-Primary_Accents_2xs'}`
                            : props.active
                                ? 'bg-Primary_Accents_2xs text-Primary'
                                : 'text-Secondary hover:bg-Primary_Accents_3xs hover:text-Primary'
                    }`}
                >
                    {!isResume && (
                        <span
                            aria-hidden="true"
                            className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-Primary transition ease-in-out duration-250 ${props.active ? 'opacity-100' : 'opacity-0'}`}
                        />
                    )}
                    {label}
                </div>
            </ScrollLink>
        );
    }

    return (
        <ScrollLink
            to={props.sectID}
            href={href}
            spy={true}
            smooth={true}
            offset={0}
            duration={1500}
            onClick={handleClick}
            className={`${props.style} ${props.active && isResume && 'bg-Primary'} cursor-pointer flex flex-col group hover overflow-hidden`}
        >
            <div
                data-name={ToSnakeCase(props.navItem)}
                className={`group relative overflow-hidden flex justify-center items-center py-1 min-w-[90px] w-fit gap-3 cursor-pointer transition ease-in-out duration-500
                    ${props.active ? 'text-Primary' : ''} hover:text-Primary ${props.active && isResume && 'text-white'}`}
                style={!props.active ? { color: 'var(--site-text-white)' } : undefined}
            >
                {props.icon_1 && props.icon_2 && (
                    <span className="flex-shrink-0 pointer-events-none">
                        <img src={props.active ? props.icon_1 : props.icon_2} alt="nav_icons" className="w-4 h-4" />
                    </span>
                )}
                {label}

                {!isResume && <div className={`group-hover:block ${props.active ? 'block' : 'hidden'} w-1/2 border-2 border-Primary animate-slide_right absolute bottom-0 rounded-md transition ease-in-out duration-500`}></div>}
            </div>
        </ScrollLink>
    );
};

const mapStateToProps = (state: any) => ({
    darkMode: state.app.darkMode,
    sectionInView: state.app.sectionInView
})

export default connect(mapStateToProps, null)(NavItem);
